import { Type } from 'class-transformer';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { ClassType } from "src/modules/utils/shared/class-type";
import { Objects, ObjectsInternal } from "src/modules/utils/shared/objects";
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { FrontendFieldError } from "./FrontendFieldError";
import { FrontendField } from './FrontendField';
import { FrontendFieldType } from './FrontendFieldType';
import { CustomInitializer } from '../shared/custom-initializer.interface';

export class FrontendForm implements CustomInitializer {

    //netgen start
    @Type(() => FrontendField)
    fields: FrontendField[];
    useAbsolutePositions = false;
    mandatoryFieldsRedAsterisk = false;
    focusFieldId: string;
    submitOnEnter = false;
    validateOnChange = false;
    //netgen end
    additionalValidation: (form: FrontendForm) => FrontendFieldError[];

    constructor(fields: FrontendField[], options: Partial<FrontendForm> = null) {
        this.fields = fields != null ? Arrays.withoutNull(fields) : null;
        Object.assign(this, options);
        this.customInitializer();
    }

    customInitializer(): void {
        for (let field of this.getAllFields()) {
            field.form = this;
        }        
    }

    getAllFields(): FrontendField[] {
        let result: FrontendField[] = [];
        for (let field of this.fields ?? []) {
            result.push(field);
            result = result.concat(field.getDescendents());
        }
        return result;
    }

    getField(id: string, throwIfMissing = true): FrontendField {
        let result = this.getAllFields().find(f => f.id === id);
        if (result == null && throwIfMissing) {
            throw new Error("Das Feld mit dem Namen '" + id + "' wurde nicht gefunden. Bekannte Feldnamen: " + Arrays.itemsToString(Arrays.sort(this.fields.map(f => f.id))));
        }
        return result;
    }

    
    getSubmitButton(): FrontendField {
        return this.getAllFields().find(f => f.type == FrontendFieldType.button && f.submitButton);
    }

    getValue(id: string, useDropdownInternalObject = true): unknown {
        return this.getField(id).getValue(useDropdownInternalObject);
    }

    getValueBool(id: string): boolean {
        return this.getField(id).getValueBool();
    }

    getValueDate(id: string): Date {
        return this.getField(id).getValueDate();
    }

    getValueNumber(id: string): number {
        return this.getField(id).getValueNumber();
    }

    getValueObject<T>(id: string, type: ClassType<T>, allowNull = true): T {
        return this.getValue(id) as T; //Objects.castOrThrow(this.getField(id), type, allowNull); //TODO Fehler wenn es sich um ein Plain Objekt handelt mit denselben Feldern mit T
    }

    getValueString(id: string): string {
        return this.getField(id).getValueString();
    }

    setValue(id: string, value: any, callValueChangedByUser?: boolean): void {
        this.getField(id).setValue(value, callValueChangedByUser);
    }

    getMainField(field: FrontendField): FrontendField {
        return Utils.isNoe(field.guiGroup) ? field : this.fields.find(f => f.guiGroup === field.guiGroup);
    }

    getHeight(): number {
        return this.fields.map(field => field.getMaxPosition()).reduce((p1, p2) => p1.max(p2.x, p2.y)).y;
    }

    getWidth(): number {
        return this.fields.map(field => field.getMaxPosition()).reduce((p1, p2) => p1.max(p2.x, p2.y)).x;
    }

    fill(from: any, onlyIfHasProperty?: boolean): void {
        for (let field of this.fields) {
            if (!field.noMapping) {
                let value = Objects.getPropertyNested(from, field.id, onlyIfHasProperty)
                if (onlyIfHasProperty && value == ObjectsInternal.PROPERTY_NOT_FOUND) {
                    continue;
                }
                field.setValue(value, false, from);
            }
        }
        for (let field of this.fields) {
            if (field.callValueChangedAfterLoad) {
                field.valueWasChanged(null, false);
            }
        }
        if (this.validateOnChange) {
            this.validate(false);
        }
    }

    finishEditing(): void {
        for (let field of this.fields) {
            field.finishEditing();
        }
    }

    get<T>(to: T, finishEditing = true): T {
        if (finishEditing) {
            this.finishEditing();
        }
        for (let field of this.getAllFields()) {
            if (!field.noMapping) {
                Objects.setPropertyNested(to, field.id, field.getValue(true, to));
            }
        }
        return to;
    }

    getAsObject(finishEditing = true): OrdinaryObject {
        return this.get<OrdinaryObject>({} as OrdinaryObject, finishEditing);
    }

    validate(finishEditing = true): FrontendFieldError[] {
        if (finishEditing) {
            this.finishEditing();
        }
        let result = Arrays.explode(this.fields, f => f.validate(this));
        if (this.additionalValidation != null) {
            result = [...result, ...this.additionalValidation(this)];
        }

        for (let field of this.fields) {
            field.errors = null;
        }
        for (let error of result) {
            let field = this.fields.find(f => f.id == error.fieldId);
            if (field != null) {
                field.errors = [...field.errors ?? [], error];
            }
        }

        return result;
    }

    getGroupedFields(): FrontendField[][] {
        let result: FrontendField[][] = [];
        for (let field of this.fields) {
            let group = Utils.isNoe(field.guiGroup) ? null : result.find(g => g[0].guiGroup == field.guiGroup);
            if (group == null) {
                result.push([field]);
            }
            else {
                group.push(field);
            }
        }
        return result;
    }

}